import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { usePerspective } from '../../contexts/PerspectiveContext';
import { useRiskAssessment } from '../../contexts/RiskAssessmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SavingIndicator } from '../../components/ui/SavingIndicator';
import { useAutoSave } from '../../hooks/useAutoSave';
import TCVAllocationCard from '../../components/pursuit/TCVAllocationCard';
import { DocumentHistory } from '../../components/ai/DocumentHistory';
import RequirementsTraceMatrix from '../../components/pursuit/RequirementsTraceMatrix';
import PartnerScoringPanel from '../../components/pursuit/PartnerScoringPanel';
import PartnerScoringRowControls from '../../components/pursuit/PartnerScoringRowControls';
import { PursuitRiskWidgetSafe as PursuitRiskWidget } from '../../components/ai/PursuitRiskWidget';
import { toast } from 'sonner';
import { 
  ChevronRight,
  ArrowRight,
  Calendar,
  Building2,
  DollarSign,
  Hash,
  Tag,
  FileText,
  Link as LinkIcon,
  Clock,
  AlertCircle,
  Loader2,
  Globe,
  Shield,
  Users,
  Briefcase,
  Target,
  FolderOpen,
  Sparkles,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Info,
  TrendingUp,
  Database,
  MapPin,
  Plus,
  Trash2,
  Handshake,
  Percent,
  Save,
  MoreVertical,
  Lock,
  Unlock,
  User,
  Award,
  FileStack,
  Archive
} from 'lucide-react';
import DeletePursuitModal from '../../components/modals/DeletePursuitModal';
import ArchivePursuitModal from '../../components/modals/ArchivePursuitModal';
import {
  OriginBadge,
  CollaborationFootnote,
} from '../../components/pursuit/CollaborationBadges';
import { PhaseTracker } from '../../components/pursuit/PhaseTracker';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';
// ShredderUpload removed — Document Ingestion is the single entry point

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Contract types
const CONTRACT_TYPES = [
  { value: 'FFP', label: 'Firm Fixed Price (FFP)' },
  { value: 'T_AND_M', label: 'Time & Materials (T&M)' },
  { value: 'CPFF', label: 'Cost Plus Fixed Fee (CPFF)' },
  { value: 'IDIQ', label: 'IDIQ' },
  { value: 'BPA', label: 'BPA' },
  { value: 'OTHER', label: 'Other' }
];

// Set-aside types
const SET_ASIDE_TYPES = [
  { value: 'FULL_OPEN', label: 'Full & Open Competition' },
  { value: 'SMALL_BUSINESS', label: 'Small Business Set-Aside' },
  { value: '8A', label: '8(a)' },
  { value: 'SDVOSB', label: 'SDVOSB' },
  { value: 'WOSB', label: 'WOSB' },
  { value: 'HUBZONE', label: 'HUBZone' }
];

// Normalize SAM.gov set-aside strings to our enum values
const SAM_SET_ASIDE_MAP = {
  'Total Small Business Set-Aside': 'SMALL_BUSINESS',
  'Small Business Set-Aside': 'SMALL_BUSINESS',
  'Partial Small Business Set-Aside': 'SMALL_BUSINESS',
  'Total Small Business': 'SMALL_BUSINESS',
  '8(a) Set-Aside': '8A',
  '8(a) Set-Aside (FAR 19.8)': '8A',
  '8(a) Sole Source': '8A',
  'Service-Disabled Veteran-Owned Small Business': 'SDVOSB',
  'SDVOSB Set-Aside': 'SDVOSB',
  'SDVOSB Sole Source': 'SDVOSB',
  'Women-Owned Small Business': 'WOSB',
  'WOSB Set-Aside': 'WOSB',
  'EDWOSB Set-Aside': 'WOSB',
  'HUBZone Set-Aside': 'HUBZONE',
  'HUBZone Sole Source': 'HUBZONE',
  'Full and Open Competition': 'FULL_OPEN',
  'Full and Open': 'FULL_OPEN',
  'FULL_AND_OPEN': 'FULL_OPEN',
  'None': '',
};

function normalizeSetAside(value) {
  if (!value) return '';
  // If already one of our enum values, return as-is
  if (SET_ASIDE_TYPES.some(t => t.value === value)) return value;
  // Map SAM.gov strings to our enum
  return SAM_SET_ASIDE_MAP[value] || value;
}

// Helper function
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Format currency
function formatCurrency(value) {
  if (!value) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
}

// ================================================
// AGENCY INTELLIGENCE CARD COMPONENT (Dynamic API)
// ================================================
function AgencyIntelligenceCard({ agencyCode, agencyName, getAuthHeaders }) {
  const [intel, setIntel] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Fetch agency intel when code changes
  useEffect(() => {
    const fetchAgencyIntel = async () => {
      if (!agencyCode || agencyCode.length < 2) {
        setIntel(null);
        setHasSearched(false);
        return;
      }
      
      setIsLoading(true);
      setHasSearched(true);
      
      try {
        const response = await fetch(`${API_URL}/api/agencies/by-code/${agencyCode}`, {
          headers: getAuthHeaders ? getAuthHeaders() : { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          setIntel(data?.found === false ? null : data);
        } else {
          setIntel(null);
        }
      } catch (err) {
        console.error('Failed to fetch agency intel:', err);
        setIntel(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Debounce the fetch
    const timer = setTimeout(fetchAgencyIntel, 500);
    return () => clearTimeout(timer);
  }, [agencyCode, getAuthHeaders]);
  
  if (!agencyCode) {
    return (
      <Card className="border-2 border-dashed border-slate-200 bg-slate-50/50" data-testid="agency-intel-empty">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-slate-200 rounded-xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-slate-400" />
            </div>
            <div>
              <p className="text-slate-500 font-medium">Agency Intelligence</p>
              <p className="text-sm text-slate-400">Enter an agency code to see intelligence data</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (isLoading) {
    return (
      <Card className="border-2 border-slate-200" data-testid="agency-intel-loading">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center animate-pulse">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
            <div>
              <p className="text-slate-600 font-medium">Loading Agency Intel...</p>
              <p className="text-sm text-slate-400">Searching playbook for {agencyCode}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className={cn("border-2 overflow-hidden", intel?.border_color || "border-slate-200")} data-testid="agency-intel-card">
      {/* Agency Header */}
      <div className={cn("px-6 py-4", intel?.primary_color || "bg-slate-600")}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-3xl">
            {intel?.logo_emoji || '🏛️'}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="font-mono">{intel?.code || agencyCode}</span>
              {intel?.abbreviation && intel.abbreviation !== intel.code && (
                <>
                  <span className="text-white/50 font-light">|</span>
                  <span className="font-mono text-white/90">{intel.abbreviation}</span>
                </>
              )}
            </h3>
            <p className="text-white/80 text-sm">
              {intel?.name || agencyName || 'Federal Agency'}
            </p>
          </div>
          {intel && (
            <Badge className="ml-auto bg-white/20 text-white border-0 hover:bg-white/30">
              <Database className="w-3 h-3 mr-1" />
              Agency Playbook
            </Badge>
          )}
        </div>
      </div>
      
      {/* Intel Grid */}
      {intel ? (
        <CardContent className={cn("p-0", intel.secondary_color)}>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-slate-200/50">
            <div className="p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Department</p>
              <p className={cn("font-semibold text-sm", intel.text_color)}>{intel.department_type || 'N/A'}</p>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Annual Budget</p>
              <p className={cn("font-semibold text-sm", intel.text_color)}>{intel.annual_budget || 'N/A'}</p>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Avg Contract</p>
              <p className={cn("font-semibold text-sm", intel.text_color)}>{intel.avg_contract_size || 'N/A'}</p>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Win Rate</p>
              <p className={cn("font-semibold text-sm", intel.text_color)}>
                {intel.historical_win_rate ? `${intel.historical_win_rate}%` : 'N/A'}
              </p>
            </div>
          </div>
          
          {/* Key Vehicles */}
          {intel.key_vehicles && intel.key_vehicles.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-200/50">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Key Contract Vehicles</p>
              <div className="flex flex-wrap gap-2">
                {intel.key_vehicles.map((vehicle, idx) => (
                  <Badge key={`${vehicle}-${idx}`} className={cn("text-xs border", intel.border_color, intel.secondary_color, intel.text_color)}>
                    {vehicle}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Agency Link */}
          {intel.procurement_portal_url && (
            <div className="px-4 py-3 border-t border-slate-200/50 flex items-center justify-between">
              <span className="text-xs text-slate-500">Procurement Portal</span>
              <a 
                href={intel.procurement_portal_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={cn("text-xs font-medium flex items-center gap-1 hover:underline", intel.text_color)}
                data-testid="agency-portal-link"
              >
                Visit Portal
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </CardContent>
      ) : hasSearched ? (
        <CardContent className="p-4 bg-amber-50">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Not in Agency Playbook</p>
              <p className="text-xs text-amber-600">
                Agency "{agencyCode}" not found. Ask your admin to add it to the playbook.
              </p>
            </div>
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
}

// ================================================
// DEADLINE CARD COMPONENT
// ================================================
function DeadlineCard({ deadline, daysRemaining, urgency }) {
  const getUrgencyConfig = (urg) => {
    switch (urg) {
      case 'overdue':
        return { bg: 'bg-red-500', text: 'text-white', icon: AlertCircle, label: 'OVERDUE' };
      case 'urgent':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'URGENT' };
      case 'warning':
        return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock, label: 'APPROACHING' };
      default:
        return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2, label: 'ON TRACK' };
    }
  };
  
  const config = getUrgencyConfig(urgency);
  const IconComponent = config.icon;
  
  if (!deadline) {
    return (
      <Card className="border-2 border-dashed border-slate-200" data-testid="deadline-card-empty">
        <CardContent className="p-4 flex items-center gap-3">
          <Calendar className="w-8 h-8 text-slate-300" />
          <div>
            <p className="text-sm font-medium text-slate-500">Response Deadline</p>
            <p className="text-xs text-slate-400">Not set</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const deadlineDate = new Date(deadline);
  const formattedDate = deadlineDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = deadlineDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
  
  return (
    <Card className={cn("border-2", config.border || "border-slate-200", urgency === 'overdue' ? config.bg : '')} data-testid="deadline-card">
      <CardContent className={cn("p-4", urgency !== 'overdue' && config.bg)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", urgency === 'overdue' ? 'bg-white/20' : config.bg)}>
              <IconComponent className={cn("w-6 h-6", config.text)} />
            </div>
            <div>
              <p className={cn("text-xs uppercase tracking-wider font-semibold", config.text)}>{config.label}</p>
              <p className={cn("font-bold text-lg", urgency === 'overdue' ? 'text-white' : 'text-slate-900')} style={{ fontFamily: 'Manrope, sans-serif' }}>
                {Math.abs(daysRemaining)} days {daysRemaining < 0 ? 'overdue' : 'remaining'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={cn("text-sm font-medium", urgency === 'overdue' ? 'text-white' : 'text-slate-700')}>
              {formattedDate}
            </p>
            <p className={cn("text-xs", urgency === 'overdue' ? 'text-white/80' : 'text-slate-500')}>
              {formattedTime}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ================================================
// MAIN COMPONENT
// ================================================
export default function OpportunityIntakeLive() {
  const { pursuitId, workspaceId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user, authToken } = useAuth();
  const { getEffectiveRole, isInMockView } = usePerspective();
  
  // Role checks
  const effectiveRole = getEffectiveRole();
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN && !isInMockView;
  const isConsultant = effectiveRole === ROLES.CONSULTANT;
  const isContractorAdmin = effectiveRole === ROLES.CONTRACTOR_ADMIN;
  const isBidManager = effectiveRole === ROLES.BID_MANAGER;
  const isReviewer = effectiveRole === ROLES.REVIEWER;

  // Determine edit permissions (Admin, Consultant, Contractor Admin, Bid Manager can edit phases)
  const canEdit = isSuperAdmin || isConsultant || isContractorAdmin || isBidManager;
  // Lifecycle actions (Archive / Delete). Reviewers are read-only and must
  // never see these in the 3-dot menu.
  const canManageLifecycle = !isReviewer;
  
  // Risk Assessment HUD context
  const { updatePursuit, showHUD } = useRiskAssessment();
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  
  // Form state
  const [pursuit, setPursuit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [linkedOrganizations, setLinkedOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [organizationsFetched, setOrganizationsFetched] = useState(false);
  // All fields are always editable throughout the FAR cycle (per Super Admin)
  const sectionsLocked = false;
  const [formData, setFormData] = useState({
    opportunity_name: '',
    agency_code: '',
    agency_name: '',
    bureau_code: '',
    bureau_name: '',
    solicitation_number: '',
    naics_code: '',
    psc_code: '',
    set_aside_type: '',
    prime_or_sub: 'PRIME',
    contract_type: '',
    notice_type: '',  // v3.13.0 — RFI / RFP / RFQ / SOURCES_SOUGHT / IFB / SPECIAL_NOTICE / OTHER
    estimated_value: '',
    period_of_performance: '',
    place_of_performance: '',
    response_deadline: '',
    agency_url: '',
    description: '',
    organization_id: '',  // Client organization
    owner_name: '',       // Capture Manager
    total_volume_count: 1,
    teaming_plan: [],     // Teaming partners
    prime_metadata: {     // Prime Bidder identity
      entity_name: '',
      uei: '',
      cage_code: '',
      socioeconomic_status: ''
    }
  });

  // Agency / Bureau dropdown data — sourced from the unified Agency
  // Playbook (`/api/agencies` + `/api/agencies/bureaus?agency_code=…`).
  // Same shape and endpoints as the Pipeline create modal, so the value
  // we save (`agency_code`) is the 2-letter internal code for both flows.
  const [playbookAgencies, setPlaybookAgencies] = useState([]);
  const [agencyBureaus, setAgencyBureaus] = useState([]);
  const [loadingPlaybookAgencies, setLoadingPlaybookAgencies] = useState(false);
  const [loadingAgencyBureaus, setLoadingAgencyBureaus] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoadingPlaybookAgencies(true);
      try {
        const r = await fetch(`${API_URL}/api/agencies`, { headers: getAuthHeaders() });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setPlaybookAgencies(
          (data.items || []).map((a) => ({
            code: a.code,
            abbreviation: a.abbreviation,
            name: a.name,
          })),
        );
      } finally {
        if (!cancelled) setLoadingPlaybookAgencies(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch bureaus whenever the saved agency changes — including when
  // the form is initially hydrated from the server.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!formData.agency_code) {
        setAgencyBureaus([]);
        return;
      }
      setLoadingAgencyBureaus(true);
      try {
        const r = await fetch(
          `${API_URL}/api/agencies/bureaus?agency_code=${encodeURIComponent(formData.agency_code)}`,
          { headers: getAuthHeaders() },
        );
        if (!r.ok) return;
        const list = await r.json();
        if (cancelled) return;
        setAgencyBureaus(Array.isArray(list) ? list : []);
      } finally {
        if (!cancelled) setLoadingAgencyBureaus(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.agency_code]);
  
  // Teaming Partners helpers
  const emptyPartner = {
    partner_abbr: '',
    legal_name: '',
    address: '',
    workshare: 0,
    role: '',
    partner_org_id: '',   // link to an existing client org (consultant flow)
  };

  // Consultant's client orgs — selectable as teaming partners. Their KB
  // (capabilities, past performance, certifications) then surfaces read-only
  // in Manage Documents for this pursuit.
  const [clientOrgs, setClientOrgs] = useState([]);
  useEffect(() => {
    if (!isConsultant || !user?.organizationId || !authToken) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/clients?consultant_org_id=${encodeURIComponent(user.organizationId)}&page_size=100`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (!res.ok) return;
        const body = await res.json();
        setClientOrgs((body.items || []).map(c => ({
          id: c.id, name: c.name,
          abbr: (c.ebuy_client_code || '').toUpperCase(),
          address: [c.address_line1, c.city, c.state, c.zip_code].filter(Boolean).join(', '),
        })));
      } catch { /* non-fatal */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsultant, user?.organizationId, authToken]);

  // Pick an existing client org as a teaming partner — auto-fills identity
  // and stores the link so Manage Documents can surface that org's KB.
  const linkPartnerClient = (index, clientId) => {
    const c = clientOrgs.find(o => o.id === clientId);
    const newPlan = formData.teaming_plan.map((p, i) =>
      i === index ? {
        ...p,
        partner_org_id: clientId || '',
        ...(c ? {
          legal_name: c.name || p.legal_name,
          partner_abbr: c.abbr || p.partner_abbr || (c.name || '').slice(0, 4).toUpperCase(),
          address: c.address || p.address,
        } : {}),
      } : p,
    );
    setFormData(prev => ({ ...prev, teaming_plan: newPlan }));
    save({ teaming_plan: newPlan.filter(p => p.partner_abbr || p.legal_name) });
  };
  
  const totalWorkshare = formData.teaming_plan.reduce((sum, p) => sum + (parseInt(p.workshare) || 0), 0);
  const workshareError = totalWorkshare > 100 ? `Total workshare (${totalWorkshare}%) exceeds 100%` : null;
  // SBA limit-of-subcontracting: prime must keep ≥ 50% of work — flag the
  // pill the moment partner total crosses 50% (red instead of emerald) even
  // if it hasn't broken the 100% ceiling yet.
  const workshareSbaWarning = totalWorkshare > 50 && totalWorkshare <= 100;
  
  const addPartner = () => {
    if (formData.teaming_plan.length >= 5) return;
    const newTeamingPlan = [...formData.teaming_plan, { ...emptyPartner }];
    setFormData(prev => ({ ...prev, teaming_plan: newTeamingPlan }));
  };
  
  const removePartner = (index) => {
    const newTeamingPlan = formData.teaming_plan.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, teaming_plan: newTeamingPlan }));
    // Auto-save after removal
    save({ teaming_plan: newTeamingPlan });
  };
  
  const updatePartner = (index, field, value) => {
    const newTeamingPlan = formData.teaming_plan.map((p, i) => 
      i === index ? { ...p, [field]: field === 'workshare' ? (parseInt(value) || 0) : value } : p
    );
    setFormData(prev => ({ ...prev, teaming_plan: newTeamingPlan }));
  };
  
  const saveTeamingPlan = () => {
    if (workshareError) {
      toast.error(workshareError);
      return;
    }
    // Filter out empty partners
    const validPartners = formData.teaming_plan.filter(p => p.partner_abbr || p.legal_name);
    save({ teaming_plan: validPartners });
  };

  // Auto-save function
  const savePursuit = useCallback(async (data) => {
    const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error('Failed to save');
    }
    
    return response.json();
  }, [pursuitId, getAuthHeaders]);

  const { save, isSaving, lastSaved, error: saveError } = useAutoSave(savePursuit, 1000);

  // Fetch pursuit data with retry logic for race conditions after upload
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 500; // 500ms between retries
    let isCancelled = false;
    
    const fetchPursuit = async () => {
      // Wait for auth token to be available
      if (!authToken) {
        // Auth not ready yet, wait and retry
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(() => {
            if (!isCancelled) fetchPursuit();
          }, retryDelay);
        }
        return;
      }
      
      setIsLoading(true);
      
      const attemptFetch = async () => {
        try {
          const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
            headers: getAuthHeaders()
          });
          
          if (!response.ok) {
            // If pursuit not found or auth error and we have retries left, wait and retry
            if ((response.status === 404 || response.status === 401) && retryCount < maxRetries) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              return attemptFetch();
            }
            throw new Error('Pursuit not found');
          }
          
          const data = await response.json();
          if (!isCancelled) {
            // Also fetch evaluation for HUD score calculation
            try {
              const evalRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/evaluation`, {
                headers: getAuthHeaders()
              });
              if (evalRes.ok) {
                const evalData = await evalRes.json();
                data.evaluation = evalData;
              }
            } catch (evalErr) {
              // v3.20.1 — non-fatal: pursuit still loads without evaluation data;
              // log so future regressions are visible.
              console.warn('[OpportunityIntakeLive] evaluation fetch failed:', evalErr);
            }
            
            setPursuit(data);
            // Update Risk Assessment HUD with pursuit + evaluation data
            updatePursuit(data);
            showHUD();
            
            // Normalize set-aside from SAM.gov format to our enum
            const normalizedSetAside = normalizeSetAside(data.set_aside_type);
            
            // Populate form (including prime_metadata)
            setFormData({
              opportunity_name: data.opportunity_name || '',
              agency_code: data.agency_code || '',
              agency_name: data.agency_name || '',
              bureau_code: data.bureau_code || '',
              bureau_name: data.bureau_name || '',
              solicitation_number: data.solicitation_number || '',
              naics_code: data.naics_code || '',
              psc_code: data.psc_code || '',
              set_aside_type: normalizedSetAside,
              prime_or_sub: data.prime_or_sub || 'PRIME',
              contract_type: data.contract_type || '',
              notice_type: data.notice_type || '',
              estimated_value: data.estimated_value || '',
              period_of_performance: data.period_of_performance || '',
              place_of_performance: data.place_of_performance || '',
              response_deadline: data.response_deadline ? data.response_deadline.split('T')[0] : '',
              agency_url: data.agency_url || '',
              description: data.description || '',
              organization_id: data.organization_id || '',
              owner_name: data.owner_name || '',
              total_volume_count: data.total_volume_count || 1,
              teaming_plan: data.teaming_plan || [],
              prime_metadata: {
                entity_name: data.prime_metadata?.entity_name || '',
                uei: data.prime_metadata?.uei || '',
                cage_code: data.prime_metadata?.cage_code || '',
                socioeconomic_status: data.prime_metadata?.socioeconomic_status || ''
              }
            });
            
            // Lock top sections if key fields were pre-populated from modal
            const hasModalData = !!(data.agency_code || data.estimated_value || data.response_deadline);
            // Fields always editable — no locking
            
            // If set-aside was normalized (different from DB), save the normalized value
            if (normalizedSetAside && normalizedSetAside !== data.set_aside_type) {
              save({ set_aside_type: normalizedSetAside });
            }
          }
        } catch (err) {
          // Only show error toast after all retries are exhausted
          if (retryCount >= maxRetries && !isCancelled) {
            toast.error(err.message);
          }
          throw err;
        }
      };
      
      try {
        await attemptFetch();
      } catch (err) {
        // v3.20.1 — attemptFetch already toasts/handles 401/404 in its body;
        // this finally-block exists only for setIsLoading. Log unexpected
        // exceptions so they don't disappear silently in prod.
        console.warn('[OpportunityIntakeLive] attemptFetch unhandled:', err);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    if (pursuitId) {
      fetchPursuit();
    }
    
    return () => {
      isCancelled = true;
    };
  }, [pursuitId, authToken, getAuthHeaders]);

  // Handle pursuit deletion
  const handlePursuitDeleted = (result) => {
    if (result.action_taken === 'start_over_reset') {
      // Just refresh the page for Start Over
      window.location.reload();
    } else {
      // Redirect to pipeline for actual deletion
      navigate(`/${workspaceId}/pipeline`);
    }
  };

  // Fetch linked organizations for client selector (for Super Admin/Consultant)
  // FIX: Wait for user object to be fully loaded before fetching
  useEffect(() => {
    const fetchLinkedOrgs = async () => {
      // Ensure user is fully loaded with role and organizationId
      if (!authToken || !user?.role || !user?.organizationId) return;
      
      // Only for Super Admin or Consultant
      if (user.role !== ROLES.SUPER_ADMIN && user.role !== ROLES.CONSULTANT) return;
      
      setLoadingOrganizations(true);
      try {
        const res = await fetch(`${API_URL}/api/clients?consultant_org_id=${user.organizationId}`, {
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          setLinkedOrganizations(data.items || []);
        }
      } catch (err) {
        console.error('Failed to fetch linked organizations:', err);
      } finally {
        setLoadingOrganizations(false);
        setOrganizationsFetched(true);
      }
    };
    
    fetchLinkedOrgs();
  }, [authToken, user?.role, user?.organizationId, getAuthHeaders]);

  // Auto-populate prime_metadata from linked org when:
  // - role is PRIME, org is linked, but prime_metadata is empty
  // - This fixes existing pursuits that were created before the auto-populate fix
  useEffect(() => {
    if (!linkedOrganizations.length || !formData.organization_id) return;
    if (formData.prime_or_sub !== 'PRIME') return;
    // Only auto-populate if prime_metadata is empty
    if (formData.prime_metadata?.entity_name) return;
    
    const selectedOrg = linkedOrganizations.find(org => org.id === formData.organization_id);
    if (!selectedOrg) return;
    
    const newPrimeMetadata = {
      entity_name: selectedOrg.name || '',
      uei: selectedOrg.uei || '',
      cage_code: selectedOrg.cage_code || '',
      socioeconomic_status: selectedOrg.socioeconomic_status || ''
    };
    
    setFormData(prev => ({ ...prev, prime_metadata: newPrimeMetadata }));
    save({ prime_metadata: newPrimeMetadata });
  }, [linkedOrganizations, formData.organization_id, formData.prime_or_sub]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle field change with auto-save
  const handleFieldChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    // Prepare update payload
    const updatePayload = { [field]: value };

    // Handle special conversions
    if (field === 'estimated_value' && value) {
      updatePayload[field] = parseFloat(value);
    }
    if (field === 'response_deadline' && value) {
      updatePayload[field] = new Date(value).toISOString();
    }

    // v3.10.6: also reflect the change in the top-level `pursuit` state
    // so read-only cards (DeadlineCard, Risk HUD, etc.) re-render with
    // the new value INSTANTLY instead of waiting for a refetch that
    // never comes. `useAutoSave` doesn't surface the PATCH response
    // back, so the fetched `pursuit` object would otherwise stay stale
    // for the rest of the session — which is exactly what "not getting
    // picked up" feels like.
    setPursuit(prev => (prev ? { ...prev, ...updatePayload } : prev));

    // Trigger auto-save
    save(updatePayload);
  };

  // v3.18.13 — Lifecycle update: Intake → Ingestion (not Go/No-Go).
  // Document Ingestion is the new step 2 in the federal capture flow.
  const handleNavigateNext = () => {
    navigate(`/${workspaceId}/pursuit/${pursuitId}/ingestion`);
  };

  if (isLoading || !authToken) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-slate-600">Loading pursuit...</span>
        </div>
      </div>
    );
  }

  if (!pursuit) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-700">Pursuit not found</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate(`/${workspaceId}/pipeline`)}
            >
              Back to Pipeline
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="opportunity-intake-live" className="min-h-screen bg-slate-50">
      {/* Header — v3.18.20 centered to max-w-7xl so it aligns with body */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <span>Pipeline</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-700 font-medium">Opportunity Intake</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formData.opportunity_name || 'New Opportunity'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Capture opportunity details. Changes save automatically.
            </p>
            {/* Collaboration context — surfaces the invitation arrangement
                between contractor and consultancy directly on the workspace. */}
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {pursuit.origin === 'INVITED' && (
                <OriginBadge pursuit={pursuit} viewerRole={user?.role} size="lg" />
              )}
              <CollaborationFootnote
                pursuit={pursuit}
                viewerRole={user?.role}
                viewerOrgId={user?.organizationId}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to={`/${workspaceId}/pursuit/${pursuitId}/ingestion`}>
              <Button variant="outline" className="gap-2">
                <FolderOpen className="w-4 h-4" />
                Manage Documents
              </Button>
            </Link>
            <SavingIndicator 
              isSaving={isSaving} 
              lastSaved={lastSaved} 
              error={saveError} 
            />
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
              <Target className="w-3 h-3 mr-1" />
              {pursuit.status}
            </Badge>
            
            {/* 3-dot Menu — lifecycle actions. Hidden entirely for Reviewer. */}
            {canManageLifecycle && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="pursuit-actions-menu">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {!pursuit.archived_at && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => setShowArchiveModal(true)}
                      data-testid="archive-pursuit-btn"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archive Pursuit
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="text-red-600 cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    data-testid="delete-pursuit-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Pursuit
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Delete Pursuit Modal */}
      <DeletePursuitModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        pursuit={pursuit}
        onDeleted={handlePursuitDeleted}
      />

      {/* Archive Pursuit Modal */}
      <ArchivePursuitModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        pursuit={pursuit}
        onArchived={(data) => {
          // After archive, redirect user back to the pipeline — the pursuit
          // no longer counts as active, so Intake for it is effectively a
          // read-only historical view.
          if (data?.pursuit) {
            setPursuit(prev => ({ ...prev, archived_at: data.pursuit.archived_at }));
          }
          navigate(`/${workspaceId}/pipeline`);
        }}
      />

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <PursuitLifecycleStepper />
          
          {/* FAR Lifecycle Phase Tracker */}
          <Card className="border-slate-200 shadow-sm" data-testid="phase-tracker-card">
            <CardContent className="p-4">
              <PhaseTracker
                pursuitId={pursuitId}
                currentPhase={pursuit.procurement_phase}
                authToken={authToken}
                onPhaseChange={(newPhase) => {
                  // Update local state when phase changes
                  setPursuit(prev => ({ ...prev, procurement_phase: newPhase }));
                }}
                canEdit={canEdit}
              />
            </CardContent>
          </Card>
          
          {/* TOP ROW: Agency Intel + Deadline */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AgencyIntelligenceCard 
              agencyCode={formData.agency_code} 
              agencyName={formData.agency_name}
              getAuthHeaders={getAuthHeaders}
            />
            <DeadlineCard 
              deadline={pursuit.response_deadline}
              daysRemaining={pursuit.days_remaining}
              urgency={pursuit.deadline_urgency}
            />
          </div>

          {/* OPPORTUNITY NAME - Hero Field */}
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-basic-info">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  Opportunity Details
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Client Organization & Role - for Super Admin / Consultant */}
              {(isSuperAdmin || isConsultant) && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Client Organization Dropdown */}
                    <div>
                      <Label htmlFor="client_org" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        Client Organization
                      </Label>
                      
                      {/* Loading state */}
                      {loadingOrganizations && (
                        <div className="flex items-center gap-2 p-3 mt-1.5 border rounded-md bg-white">
                          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                          <span className="text-sm text-slate-500">Loading...</span>
                        </div>
                      )}
                      
                      {/* Empty state */}
                      {!loadingOrganizations && organizationsFetched && linkedOrganizations.length === 0 && (
                        <div className="p-3 mt-1.5 border border-dashed border-amber-300 rounded-lg bg-amber-50">
                          <p className="text-sm font-medium text-amber-800">No clients</p>
                          <Link 
                            to={`/${workspaceId}/clients`}
                            className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-teal-600 hover:text-teal-700"
                          >
                            <Plus className="w-3 h-3" />
                            Add Client
                          </Link>
                        </div>
                      )}
                      
                      {/* Normal state */}
                      {!loadingOrganizations && linkedOrganizations.length > 0 && (
                        <Select
                          value={formData.organization_id || ''}
                          onValueChange={(v) => {
                            const updates = { organization_id: v };
                            // Auto-populate prime_metadata if role is PRIME
                            if (formData.prime_or_sub === 'PRIME') {
                              const selectedOrg = linkedOrganizations.find(org => org.id === v);
                              if (selectedOrg) {
                                updates.prime_metadata = {
                                  entity_name: selectedOrg.name || '',
                                  uei: selectedOrg.uei || '',
                                  cage_code: selectedOrg.cage_code || '',
                                  socioeconomic_status: selectedOrg.socioeconomic_status || ''
                                };
                              }
                            }
                            setFormData(prev => ({ ...prev, ...updates }));
                            save(updates);
                          }}
                        >
                          <SelectTrigger id="client_org" className="mt-1.5" data-testid="client-org-select">
                            <SelectValue placeholder={pursuit?.client_name || 'Select client...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {linkedOrganizations.map(org => (
                              <SelectItem key={org.id} value={org.id}>
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-slate-400" />
                                  {org.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Role Dropdown */}
                    <div>
                      <Label htmlFor="prime_or_sub" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-slate-500" />
                        Pursuit Role
                      </Label>
                      <Select 
                        value={formData.prime_or_sub} 
                        onValueChange={(value) => {
                          const updates = { prime_or_sub: value };
                          // Auto-populate from client org when PRIME
                          if (value === 'PRIME' && formData.organization_id) {
                            const selectedOrg = linkedOrganizations.find(org => org.id === formData.organization_id);
                            if (selectedOrg) {
                              updates.prime_metadata = {
                                entity_name: selectedOrg.name || '',
                                uei: selectedOrg.uei || '',
                                cage_code: selectedOrg.cage_code || '',
                                socioeconomic_status: selectedOrg.socioeconomic_status || ''
                              };
                            }
                          } else if (value !== 'PRIME') {
                            // Clear prime_metadata when SUB/TEAMING — user will fill Prime Bidder manually
                            updates.prime_metadata = { entity_name: '', uei: '', cage_code: '', socioeconomic_status: '' };
                          }
                          setFormData(prev => ({ ...prev, ...updates }));
                          save(updates);
                        }}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="select-prime-sub">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PRIME">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-emerald-600" />
                              Prime Contractor
                            </div>
                          </SelectItem>
                          <SelectItem value="SUB">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-blue-600" />
                              Subcontractor
                            </div>
                          </SelectItem>
                          <SelectItem value="TEAMING">
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-purple-600" />
                              Teaming Partner
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Prime Status Indicator */}
                  {formData.prime_or_sub === 'PRIME' && formData.organization_id && (
                    <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <p className="text-sm text-emerald-700">
                        <strong>{linkedOrganizations.find(o => o.id === formData.organization_id)?.name}</strong> will bid as Prime Contractor
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <Label htmlFor="opportunity_name" className="text-sm font-medium text-slate-700">Opportunity Name *</Label>
                <Input
                  id="opportunity_name"
                  value={formData.opportunity_name}
                  onChange={(e) => handleFieldChange('opportunity_name', e.target.value)}
                  placeholder="e.g., NASA IT Support Services"
                  className={cn("mt-1.5 h-12 text-lg font-medium", sectionsLocked && formData.opportunity_name && "bg-slate-50 border-slate-200")}
                  data-testid="input-opportunity-name"
                  disabled={sectionsLocked && !!formData.opportunity_name}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="solicitation_number" className="text-sm font-medium text-slate-700">Solicitation #</Label>
                  <div className="relative mt-1.5">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="solicitation_number"
                      value={formData.solicitation_number}
                      onChange={(e) => handleFieldChange('solicitation_number', e.target.value)}
                      placeholder="NNG24SEWP60001"
                      className={cn("pl-10", sectionsLocked && formData.solicitation_number && "bg-slate-50")}
                      data-testid="input-solicitation-number"
                      disabled={sectionsLocked && !!formData.solicitation_number}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="naics_code" className="text-sm font-medium text-slate-700">NAICS Code</Label>
                  <div className="relative mt-1.5">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="naics_code"
                      value={formData.naics_code}
                      onChange={(e) => handleFieldChange('naics_code', e.target.value)}
                      placeholder="541519"
                      className={cn("pl-10", sectionsLocked && formData.naics_code && "bg-slate-50")}
                      data-testid="input-naics-code"
                      disabled={sectionsLocked && !!formData.naics_code}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="psc_code" className="text-sm font-medium text-slate-700">PSC Code</Label>
                  <div className="relative mt-1.5">
                    <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="psc_code"
                      value={formData.psc_code}
                      onChange={(e) => handleFieldChange('psc_code', e.target.value.toUpperCase())}
                      placeholder="D302"
                      className={cn("pl-10 uppercase", sectionsLocked && formData.psc_code && "bg-slate-50")}
                      data-testid="input-psc-code"
                      disabled={sectionsLocked && !!formData.psc_code}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="agency_url" className="text-sm font-medium text-slate-700">SAM.gov / Portal URL</Label>
                  <div className="relative mt-1.5">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="agency_url"
                      value={formData.agency_url}
                      onChange={(e) => handleFieldChange('agency_url', e.target.value)}
                      placeholder="https://sam.gov/opp/..."
                      className={cn("pl-10", sectionsLocked && formData.agency_url && "bg-slate-50")}
                      data-testid="input-agency-url"
                      disabled={sectionsLocked && !!formData.agency_url}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AGENCY & CLASSIFICATION */}
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-agency">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-emerald-600" />
                </div>
                Agency & Classification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agency_code" className="text-sm font-medium text-slate-700">Agency</Label>
                  <Select
                    value={formData.agency_code}
                    onValueChange={(v) => {
                      const a = playbookAgencies.find((x) => x.code === v);
                      // Multi-field update: agency change clears the
                      // bureau pair as well so we don't carry stale data.
                      // Auto-save is triggered explicitly because Select
                      // bypasses `handleFieldChange`.
                      setFormData((prev) => ({
                        ...prev,
                        agency_code: v,
                        agency_name: a?.name || '',
                        bureau_code: '',
                        bureau_name: '',
                      }));
                      save({
                        agency_code: v,
                        agency_name: a?.name || '',
                        bureau_code: '',
                        bureau_name: '',
                      });
                    }}
                    disabled={sectionsLocked && !!formData.agency_code}
                  >
                    <SelectTrigger
                      id="agency_code"
                      data-testid="input-agency-code"
                      className={cn("mt-1.5", sectionsLocked && formData.agency_code && "bg-slate-50")}
                    >
                      {loadingPlaybookAgencies ? (
                        <span className="flex items-center gap-2 text-slate-400">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Loading agencies...
                        </span>
                      ) : (
                        <SelectValue placeholder="Select agency..." />
                      )}
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px]">
                      {playbookAgencies.map((a) => (
                        <SelectItem key={a.code} value={a.code}>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] px-1 py-0 font-mono">
                              {/* Dual-badge: CODE | ABBRV. CODE stays
                                   leftmost so the bureau-code prefix
                                   relationship (e.g. HE → HE36) is
                                   visually obvious. */}
                              {a.abbreviation ? `${a.code} | ${a.abbreviation}` : a.code}
                            </Badge>
                            <span className="truncate max-w-[260px]">{a.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Drives Agency Intelligence and bureau list</p>
                </div>
                <div>
                  <Label htmlFor="bureau_code" className="text-sm font-medium text-slate-700">Bureau / Sub-Agency</Label>
                  <Select
                    value={formData.bureau_code}
                    onValueChange={(v) => {
                      const b = agencyBureaus.find((x) => x.code === v);
                      setFormData((prev) => ({
                        ...prev,
                        bureau_code: v,
                        bureau_name: b?.name || '',
                      }));
                      save({
                        bureau_code: v,
                        bureau_name: b?.name || '',
                      });
                    }}
                    disabled={
                      (sectionsLocked && !!formData.bureau_code) ||
                      !formData.agency_code ||
                      loadingAgencyBureaus
                    }
                  >
                    <SelectTrigger
                      id="bureau_code"
                      data-testid="input-bureau-code"
                      className={cn("mt-1.5", sectionsLocked && formData.bureau_code && "bg-slate-50")}
                    >
                      {loadingAgencyBureaus ? (
                        <span className="flex items-center gap-2 text-slate-400">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Loading bureaus...
                        </span>
                      ) : !formData.agency_code ? (
                        <span className="text-slate-400">Select an agency first</span>
                      ) : agencyBureaus.length === 0 ? (
                        <span className="text-slate-400">No bureaus on file (optional)</span>
                      ) : (
                        <SelectValue placeholder="Select bureau (optional)..." />
                      )}
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px]">
                      {agencyBureaus.map((b) => (
                        <SelectItem key={b.code} value={b.code}>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] px-1 py-0 font-mono">
                              {b.code}
                            </Badge>
                            <span className="truncate max-w-[260px]">{b.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="set_aside_type" className="text-sm font-medium text-slate-700">Set-Aside Type</Label>
                  <Select 
                    value={formData.set_aside_type} 
                    onValueChange={(value) => handleFieldChange('set_aside_type', value)}
                    disabled={sectionsLocked && !!formData.set_aside_type}
                  >
                    <SelectTrigger className={cn("mt-1.5", sectionsLocked && formData.set_aside_type && "bg-slate-50")} data-testid="select-set-aside">
                      <SelectValue placeholder="Select set-aside type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SET_ASIDE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="contract_type" className="text-sm font-medium text-slate-700">Contract Type</Label>
                  <Select 
                    value={formData.contract_type} 
                    onValueChange={(value) => handleFieldChange('contract_type', value)}
                    disabled={sectionsLocked && !!formData.contract_type}
                  >
                    <SelectTrigger className={cn("mt-1.5", sectionsLocked && formData.contract_type && "bg-slate-50")} data-testid="select-contract-type">
                      <SelectValue placeholder="Select contract type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FFP">Fixed Price (FFP)</SelectItem>
                      <SelectItem value="T&M">Time & Materials (T&M)</SelectItem>
                      <SelectItem value="CPFF">Cost Plus Fixed Fee (CPFF)</SelectItem>
                      <SelectItem value="CPIF">Cost Plus Incentive Fee (CPIF)</SelectItem>
                      <SelectItem value="IDIQ">IDIQ</SelectItem>
                      <SelectItem value="BPA">Blanket Purchase Agreement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* v3.14.1 — Notice Type Select removed. The FAR Lifecycle
                  Phase tracker at the top of this page (SOURCES_SOUGHT /
                  SPECIAL_NOTICE / SOLICITATION / etc.) is the single source
                  of truth for solicitation phase. The Capability Statement
                  generator now reads from `procurement_phase` directly. */}
            </CardContent>
          </Card>

          {/* PRIME BIDDER IDENTITY - Only show if Role is SUB or TEAMING */}
          {formData.prime_or_sub !== 'PRIME' && (
            <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-prime-identity">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4 text-violet-600" />
                  </div>
                  Prime Bidder Identity
                  <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    You are {formData.prime_or_sub === 'SUB' ? 'Subcontractor' : 'Teaming Partner'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    Since you're pursuing this as a {formData.prime_or_sub === 'SUB' ? 'subcontractor' : 'teaming partner'}, 
                    enter the Prime Contractor's information below.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prime_entity_name" className="text-sm font-medium text-slate-700">Prime Contractor Name *</Label>
                    <Input
                      id="prime_entity_name"
                      value={formData.prime_metadata?.entity_name || ''}
                      onChange={(e) => {
                        const newPrimeMetadata = { ...formData.prime_metadata, entity_name: e.target.value };
                        setFormData(prev => ({ ...prev, prime_metadata: newPrimeMetadata }));
                        save({ prime_metadata: newPrimeMetadata });
                      }}
                      placeholder="Prime Contractor Company Name"
                      className="mt-1.5"
                      data-testid="input-prime-entity-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="prime_uei" className="text-sm font-medium text-slate-700">Prime's UEI</Label>
                    <Input
                      id="prime_uei"
                      value={formData.prime_metadata?.uei || ''}
                      onChange={(e) => {
                        const newPrimeMetadata = { ...formData.prime_metadata, uei: e.target.value.toUpperCase() };
                        setFormData(prev => ({ ...prev, prime_metadata: newPrimeMetadata }));
                        save({ prime_metadata: newPrimeMetadata });
                      }}
                      placeholder="XXXXXXXXX123"
                      className="mt-1.5 uppercase"
                      maxLength={12}
                      data-testid="input-prime-uei"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* VALUE & TIMELINE */}
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-value">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                </div>
                Value & Timeline
                {sectionsLocked && formData.estimated_value && (
                  <Badge variant="outline" className="ml-2 text-xs border-slate-300 text-slate-500">
                    <Lock className="w-3 h-3 mr-1" />
                    Pre-filled
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="estimated_value" className="text-sm font-medium text-slate-700">Estimated Value</Label>
                  <div className="relative mt-1.5">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="estimated_value"
                      type="number"
                      value={formData.estimated_value}
                      onChange={(e) => handleFieldChange('estimated_value', e.target.value)}
                      placeholder="50000000"
                      className={cn("pl-10", sectionsLocked && formData.estimated_value && "bg-slate-50")}
                      data-testid="input-estimated-value"
                      disabled={sectionsLocked && !!formData.estimated_value}
                    />
                  </div>
                  {formData.estimated_value && (
                    <p className="text-xs text-emerald-600 mt-1 font-medium">
                      {formatCurrency(formData.estimated_value)}
                    </p>
                  )}
                </div>
                {/* Contract Type lives in Agency & Classification (removed duplicate here). */}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="response_deadline" className="text-sm font-medium text-slate-700">Response Deadline</Label>
                  <div className="relative mt-1.5">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="response_deadline"
                      type="date"
                      value={formData.response_deadline}
                      onChange={(e) => handleFieldChange('response_deadline', e.target.value)}
                      className={cn("pl-10", sectionsLocked && formData.response_deadline && "bg-slate-50")}
                      data-testid="input-response-deadline"
                      disabled={sectionsLocked && !!formData.response_deadline}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="period_of_performance" className="text-sm font-medium text-slate-700">Period of Performance (months)</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="period_of_performance"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.period_of_performance}
                      onChange={(e) => handleFieldChange('period_of_performance', e.target.value)}
                      placeholder="e.g., 60"
                      className="pr-16"
                      data-testid="input-pop"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">months</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="place_of_performance" className="text-sm font-medium text-slate-700">Place of Performance</Label>
                  <Input
                    id="place_of_performance"
                    value={formData.place_of_performance}
                    onChange={(e) => handleFieldChange('place_of_performance', e.target.value)}
                    placeholder="Washington, DC; Remote; Multiple CONUS"
                    className="mt-1.5"
                    data-testid="input-place-of-performance"
                  />
                </div>
                <div>
                  <Label htmlFor="total_volume_count" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <FileStack className="w-3.5 h-3.5 text-slate-400" />
                    Total Volumes
                  </Label>
                  <Input
                    id="total_volume_count"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.total_volume_count}
                    onChange={(e) => handleFieldChange('total_volume_count', parseInt(e.target.value) || 1)}
                    className="mt-1.5 w-24"
                    data-testid="input-total-volumes"
                  />
                  <p className="text-xs text-slate-400 mt-1">Proposal volumes to submit</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* GENERAL INFORMATION / METADATA - "Single Pane of Glass" */}
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-general-info">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Info className="w-4 h-4 text-teal-600" />
                </div>
                General Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="owner_name" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    Capture Manager
                  </Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name}
                    onChange={(e) => handleFieldChange('owner_name', e.target.value)}
                    placeholder="e.g., Sarah Chen"
                    className="mt-1.5"
                    data-testid="input-owner-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    Bureau / Sub-Agency
                  </Label>
                  {/* Read-only mirror of the cascading Bureau Select in the
                      Agency section above. Edits happen there to keep code
                      and name in sync; this row is informational only. */}
                  <div
                    className="mt-1.5 flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm"
                    data-testid="bureau-summary-readonly"
                  >
                    {formData.bureau_code ? (
                      <>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {formData.bureau_code}
                        </Badge>
                        <span className="text-slate-700 truncate">{formData.bureau_name || '—'}</span>
                      </>
                    ) : (
                      <span className="text-slate-400 text-xs italic">
                        Not set — pick a bureau in the Agency section above
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Summary tiles for quick reference */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Status</p>
                  <p className="text-sm font-semibold text-slate-700 mt-0.5">{pursuit?.status || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">FAR Phase</p>
                  <p className="text-sm font-semibold text-slate-700 mt-0.5">
                    {pursuit?.procurement_phase?.replace(/_/g, ' ') || '—'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Role</p>
                  <p className="text-sm font-semibold text-slate-700 mt-0.5">{formData.prime_or_sub || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Created</p>
                  <p className="text-sm font-semibold text-slate-700 mt-0.5">
                    {pursuit?.created_at ? new Date(pursuit.created_at).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TEAMING PARTNERS */}
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-teaming-partners">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Handshake className="w-4 h-4 text-purple-600" />
                  </div>
                  Teaming Partners
                </CardTitle>
                <div className="flex items-center gap-2">
                  {formData.teaming_plan.length > 0 && (
                    <Badge
                      className={cn(
                        "text-xs",
                        workshareError || workshareSbaWarning
                          ? "bg-red-100 text-red-700 border-red-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      )}
                      title={workshareSbaWarning && !workshareError ? "SBA limit-of-subcontracting: prime must retain ≥ 50% workshare" : undefined}
                      data-testid="teaming-partners-allocation-pill"
                    >
                      <Percent className="w-3 h-3 mr-1" />
                      {totalWorkshare}% allocated
                    </Badge>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPartner}
                    disabled={formData.teaming_plan.length >= 5}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    data-testid="add-partner-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Partner
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Define workshare allocations for teaming partners. Total workshare cannot exceed 100%.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {workshareError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="workshare-error">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700">{workshareError}</span>
                </div>
              )}
              
              {formData.teaming_plan.length === 0 ? (
                <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-lg" data-testid="no-partners">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No teaming partners added</p>
                  <p className="text-sm text-slate-400 mt-1">Click "Add Partner" to define workshare allocations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.teaming_plan.map((partner, index) => (
                    <div 
                      key={partner.id || partner.partner_name || `partner-${index}`} 
                      className="p-4 bg-slate-50 border border-slate-200 rounded-lg"
                      data-testid={`partner-row-${index}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Partner {index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePartner(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 -mt-1 -mr-2"
                          data-testid={`remove-partner-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Consultant: link an existing client org as this partner.
                          Auto-fills identity and surfaces their KB in Manage Docs. */}
                      {isConsultant && clientOrgs.length > 0 && (
                        <div className="mb-3">
                          <Label className="text-xs text-slate-600">Link an existing client (optional)</Label>
                          <Select
                            value={partner.partner_org_id || 'none'}
                            onValueChange={(v) => linkPartnerClient(index, v === 'none' ? '' : v)}
                          >
                            <SelectTrigger className="mt-1 h-9 text-sm" data-testid={`partner-client-select-${index}`}>
                              <SelectValue placeholder="Pick a client as teaming partner…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Manual entry (not a client) —</SelectItem>
                              {clientOrgs.map(o => (
                                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {partner.partner_org_id && (
                            <p className="text-[11px] text-emerald-600 mt-1">
                              Linked — this partner's capabilities, past performance &amp; certifications appear under Manage Documents.
                            </p>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <div>
                          <Label className="text-xs text-slate-600">Abbreviation</Label>
                          <Input
                            value={partner.partner_abbr}
                            onChange={(e) => updatePartner(index, 'partner_abbr', e.target.value.toUpperCase())}
                            placeholder="ACME"
                            className="mt-1 h-9 text-sm uppercase"
                            maxLength={10}
                            data-testid={`partner-abbr-${index}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs text-slate-600">Legal Name</Label>
                          <Input
                            value={partner.legal_name}
                            onChange={(e) => updatePartner(index, 'legal_name', e.target.value)}
                            placeholder="ACME Technologies, Inc."
                            className="mt-1 h-9 text-sm"
                            data-testid={`partner-legal-name-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Workshare %</Label>
                          <div className="relative mt-1">
                            <Input
                              type="number"
                              value={partner.workshare || ''}
                              onChange={(e) => updatePartner(index, 'workshare', e.target.value)}
                              placeholder="25"
                              className="h-9 text-sm pr-8"
                              min={0}
                              max={100}
                              data-testid={`partner-workshare-${index}`}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Role / Swimlane</Label>
                          <Input
                            value={partner.role || ''}
                            onChange={(e) => updatePartner(index, 'role', e.target.value)}
                            placeholder="Cybersecurity"
                            className="mt-1 h-9 text-sm"
                            data-testid={`partner-role-${index}`}
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs text-slate-600">Address (Optional)</Label>
                        <Input
                          value={partner.address || ''}
                          onChange={(e) => updatePartner(index, 'address', e.target.value)}
                          placeholder="123 Main St, City, State ZIP"
                          className="mt-1 h-9 text-sm"
                          data-testid={`partner-address-${index}`}
                        />
                      </div>
                      {/* v3.17.0 — per-partner self-scoring controls */}
                      <PartnerScoringRowControls
                        pursuitId={pursuitId}
                        partnerName={partner.legal_name}
                        index={index}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {formData.teaming_plan.length > 0 && (
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      <span className="font-medium">{formData.teaming_plan.length}</span> partner{formData.teaming_plan.length !== 1 ? 's' : ''} defined
                    </span>
                    {totalWorkshare > 0 && !workshareError && (
                      <span className="text-sm text-emerald-600">
                        <CheckCircle2 className="w-4 h-4 inline mr-1" />
                        {100 - totalWorkshare}% remaining
                      </span>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={saveTeamingPlan}
                    disabled={!!workshareError || isSaving}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="save-teaming-plan-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Teaming Plan
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* v3.12.4 — TCV Allocation card. Auto-derives Prime + TP $
              values from estimated_value × workshare. We compose a LIVE
              preview from formData so the card refreshes the moment a
              user edits a workshare cell, without waiting for save. */}
          <TCVAllocationCard
            allocation={(() => {
              const tcv = parseFloat(formData.estimated_value);
              const partners = (formData.teaming_plan || []).map((p, i) => ({
                partner_abbr: p.partner_abbr,
                legal_name: p.legal_name,
                workshare: parseInt(p.workshare, 10) || 0,
                role: p.role,
                is_small_business: !!p.is_small_business,
                socio_economic_status: p.socio_economic_status,
                value: !isNaN(tcv) ? Math.round(tcv * (parseInt(p.workshare, 10) || 0)) / 100 : null,
              }));
              const totalPartnerWs = partners.reduce((s, p) => s + p.workshare, 0);
              const primeWs = Math.max(0, 100 - totalPartnerWs);
              const unallocatedWs = Math.max(0, 100 - totalPartnerWs - primeWs);
              const warning = isNaN(tcv)
                ? 'tcv_missing'
                : totalPartnerWs > 100
                ? 'workshare_overflow'
                : (totalPartnerWs + primeWs) < 100
                ? 'workshare_underflow'
                : null;
              return {
                tcv: isNaN(tcv) ? null : tcv,
                prime: {
                  name: pursuit?.tcv_allocation?.prime?.name || 'Prime',
                  workshare: primeWs,
                  value: !isNaN(tcv) ? Math.round(tcv * primeWs) / 100 : null,
                },
                partners,
                total_partner_workshare: totalPartnerWs,
                unallocated_workshare: unallocatedWs,
                unallocated_value: !isNaN(tcv) ? Math.round(tcv * unallocatedWs) / 100 : null,
                warning,
              };
            })()}
          />

          {/* NOTES */}
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="card-notes">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 text-slate-600" />
                </div>
                Notes & Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Add any notes about this opportunity, key requirements, win themes..."
                rows={4}
                className="resize-none"
                data-testid="input-description"
              />
            </CardContent>
          </Card>

          {/* DOCUMENT UPLOAD HISTORY */}
          <DocumentHistory 
            pursuitId={pursuitId} 
            authToken={authToken} 
          />

          {/* AI INTELLIGENCE SECTION
              v3.11.12 — bumped grid breakpoint from `lg` (1024px) to `2xl`
              (1536px). On laptops ≤1440px the Requirements table now gets
              full width (was cramped to 2/3 = ~680px after sidebar + padding,
              which clipped toolbar buttons like Re-extract on the right and
              made the new Notes/PP + Task Area chips overflow). The Risk
              widget stacks below on laptops; on real desktops (2K+) it
              still sits side-by-side. */}
          <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
            {/* Capability / Requirements Matrix — pick the SOW/PWS/SOO doc and
                build the structured requirement set (replaces the old auto-scan
                Extracted Requirements table). Becomes the canonical requirements
                that partners self-score and the Compliance Matrix maps. */}
            <div className="2xl:col-span-2">
              <RequirementsTraceMatrix
                pursuitId={pursuitId}
                authToken={authToken}
              />
            </div>
            
            {/* Risk Widget — full width below table on laptops, 1/3 on 2xl+ */}
            <div>
              <PursuitRiskWidget 
                pursuitId={pursuitId} 
                authToken={authToken}
                pursuit={pursuit}
              />
            </div>
          </div>

          {/* v3.17.0 — Partner Self-Scoring (Excel round-trip) */}
          <PartnerScoringPanel
            pursuitId={pursuitId}
            primeName={pursuit?.organization_name || pursuit?.client_name || 'Prime'}
          />

          {/* ACTION BAR */}
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100/50 shadow-sm" data-testid="action-bar">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Documents on hand?
                    </h3>
                    <p className="text-sm text-slate-600">
                      Drop your RFP / RFI / Sources Sought into Document Ingestion — AI extracts requirements & evaluation factors.
                    </p>
                  </div>
                </div>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 h-auto shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 transition-all"
                  onClick={handleNavigateNext}
                  data-testid="proceed-to-ingestion-btn"
                >
                  Proceed to Document Ingestion
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
          
        </div>
      </div>
    </div>
  );
}
